import {getHighlightedLocationSuffixes, parseRomanNumeral, planLocationMatches} from './locationMatcher';

interface ParsedLocation {
  romanNumeral: string;
  romanValue: number;
  orientation?: string;
  orientationOrder: number;
}

const ORIENTATION_ORDER: { [key: string]: number } = {
  'n': 1, 'e': 2, 's': 3, 'w': 4
};

// No orientation comes before any orientation (order 0)
const NO_ORIENTATION_ORDER = 0;

export function parseLocationCode(locationCode: string): ParsedLocation | null {
  if (!locationCode) return null;

  // Match pattern: Roman numeral optionally followed by orientation (n, e, s, w)
  const matchWithOrientation = locationCode.match(/^([IVX]+)([nesw])$/i);
  const matchRomanOnly = locationCode.match(/^([IVX]+)$/i);

  let romanNumeral: string;
  let orientation: string | undefined;
  let orientationOrder: number;

  if (matchWithOrientation) {
    // Roman numeral with orientation
    romanNumeral = matchWithOrientation[1].toUpperCase();
    orientation = matchWithOrientation[2].toLowerCase();
    orientationOrder = ORIENTATION_ORDER[orientation];

    if (orientationOrder === undefined) {
      return null;
    }
  } else if (matchRomanOnly) {
    // Roman numeral without orientation
    romanNumeral = matchRomanOnly[1].toUpperCase();
    orientation = undefined;
    orientationOrder = NO_ORIENTATION_ORDER;
  } else {
    // No match
    return null;
  }

  const romanValue = parseRomanNumeral(romanNumeral);
  if (romanValue === null) {
    return null;
  }

  return {
    romanNumeral,
    romanValue,
    orientation,
    orientationOrder
  };
}

export function compareLocationCodes(a: string | undefined, b: string | undefined): number {
  // Handle undefined/null cases - no location comes first
  if (!a && !b) return 0;
  if (!a) return -1; // a (no location) comes before b (has location)
  if (!b) return 1;  // a (has location) comes after b (no location)

  const parsedA = parseLocationCode(a);
  const parsedB = parseLocationCode(b);

  // If either can't be parsed, fall back to string comparison
  if (!parsedA && !parsedB) return a.localeCompare(b);
  if (!parsedA) return 1;  // unparseable comes after parseable
  if (!parsedB) return -1; // parseable comes before unparseable

  // First compare by Roman numeral value
  if (parsedA.romanValue !== parsedB.romanValue) {
    return parsedA.romanValue - parsedB.romanValue;
  }

  // If Roman numerals are equal, compare by orientation
  // No orientation (0) comes before any orientation (1, 2, 3, 4)
  return parsedA.orientationOrder - parsedB.orientationOrder;
}

export function formatLocation(locationCode?: string, locationDescription?: string): string | undefined {
  if (!locationCode) return undefined;

  if (locationDescription) {
    return `${locationCode}, ${locationDescription}`;
  }

  return locationCode;
}

/**
 * Parses a comma-separated location code string into an array of individual codes.
 * E.g., "VII, X, XI, XIV" -> ["VII", "X", "XI", "XIV"]
 * Note: This returns raw codes without range expansion. For expanded codes, use getHighlightedLocationSuffixes.
 */
export function parseLocationCodes(locationCode?: string): string[] {
  if (!locationCode) return [];
  return locationCode.split(',').map(code => code.trim()).filter(code => code.length > 0);
}

/**
 * Checks if a plan location element (e.g., "XI") matches a location code field.
 * Supports:
 * - Simple matching: "XI" matches "XI, XII, XIII"
 * - Range expansion: "XI" matches "X-XII" (expands to X, XI, XII)
 * - Orientation stripping: "XI" matches "XIw, XIIe" (strips w, e suffixes)
 * - Exact range matching: "X-XII" matches "X-XII, XVII"
 *
 * @param singleCode The location code from the plan (e.g., "XI" from "location-XI")
 * @param locationCodeField The comma-separated location code field from the artistic object
 */
export function locationCodeMatches(singleCode: string, locationCodeField?: string): boolean {
  if (!singleCode || !locationCodeField) return false;
  return planLocationMatches(`location-${singleCode}`, locationCodeField);
}

// Re-export for convenience
export {getHighlightedLocationSuffixes} from './locationMatcher';
